import "./changePassword.css";
import React, { useState, useEffect } from "react";
import { Form, Row, Col, Button, ProgressBar } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useAppKeycloak } from "../../keycloak/keycloakUtils";
import { ServiceFactory } from "../../Api/Services/ServiceFactory";
import { ServiceNames } from "../../Api/registry/Services";
import { useToast } from "../ToastContext/ToastContext";

const ChangePassword = () => {
  const { t } = useTranslation(["usersetting"]);
  const { keycloak } = useAppKeycloak();
  const { showToast } = useToast();
  const [userService, setUserService] = useState(null);
  const [errors, setErrors] = useState({});
  const [userData, setUserData] = useState({
    userid: "",
    oldpassword: "",
    newpassword: "",
    confirmpassword: "",
  });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [focusPassword, setFocusPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    checks: {
      length: false,
      lowercase: false,
      uppercase: false,
      number: false,
      special: false,
    },
  });

  const userId = keycloak.tokenParsed?.sub;

  useEffect(() => {
    const initializeService = async () => {
      const service = await ServiceFactory.getService(ServiceNames.UserService);
      setUserService(service);
    }
    initializeService();
    if (userId) {
      setUserData((prevData) => ({
        ...prevData,
        userid: userId,
      }));
    }
  }, [userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedValue = value;

    setUserData((prev) => {
      const updatedForm = { ...prev, [name]: updatedValue };

      // Aggiorna forza password SOLO per newpassword
      if (name === 'newpassword') {
        evaluatePassword(updatedValue);
      }

      // Controllo live corrispondenza password
      if (
        (name === 'newpassword' && updatedForm.confirmpassword) ||
        (name === 'confirmpassword' && updatedForm.newpassword)
      ) {
        if (updatedForm.newpassword !== updatedForm.confirmpassword) {
          setErrors((prev) => ({
            ...prev,
            confirmpassword: t('usersetting:messages.passwordnotmatch'),
          }));
        } else {
          setErrors((prev) => {
            const newErr = { ...prev };
            delete newErr.confirmpassword;
            return newErr;
          });
        }
      }

      return updatedForm;
    });
  };

  const validateForm = (userData, t) => {
    const validationErrors = {};

    if (!userData.oldpassword) {
      validationErrors.oldpassword = t('usersetting:messages.passwordrequired')
    }

    const passwordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&.#^()_+\-=\[\]{};':"\\|,.<>\/?])[A-Za-z\d@$!%*?&.#^()_+\-=\[\]{};':"\\|,.<>\/?]{8,}$/;

    if (!userData.newpassword) validationErrors.newpassword = t('usersetting:messages.passwordrequired');
    else if (!passwordRegex.test(userData.newpassword)) validationErrors.newpassword = t('usersetting:messages.passwordpolicymessage');

    if (userData.newpassword !== userData.confirmpassword) validationErrors.confirmpassword = t('usersetting:messages.passwordnotmatch');

    return validationErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm(userData, t);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const updatePasswordRequest = {
        oldPassword: userData.oldpassword,
        newPassword: userData.newpassword
      };

      await userService.changePassword(updatePasswordRequest);

      // Reset form dopo il successo
      setUserData({
        userid: userId,
        oldpassword: "",
        newpassword: "",
        confirmpassword: ""
      });
      setErrors({});
      setPasswordStrength({
        score: 0,
        checks: {
          length: false,
          lowercase: false,
          uppercase: false,
          number: false,
          special: false,
        },
      });

      showToast(t("usersetting:messages.success"), "success");
    } catch (error) {
      showToast(error.message || t("error:wrong"), "error");
    }
  };

  const togglePasswordVisibility = (field) => {
    if (field === 'oldpassword') setShowOldPassword((prev) => !prev);
    else if (field === 'newpassword') setShowNewPassword((prev) => !prev);
    else setShowConfirmPassword((prev) => !prev);
  };

  const evaluatePassword = (password) => {
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[^A-Za-z\d]/.test(password),
    };
    const score = Object.values(checks).filter(Boolean).length;
    setPasswordStrength({ score, checks });
  };

  return (
    <Form className="password-form" onSubmit={handleSubmit} noValidate>
      <Row>
        <Col sm={7}>
          <Form.Group
            as={Row}
            className="mb-3"
            onFocus={() => setFocusPassword(true)}
            onBlur={() => setFocusPassword(false)}
          >
            <Col sm={3}>
              <Form.Label column sm={2} style={{ textAlign: 'center', color: 'white' }}>
                {t("usersetting:caption.oldpassword")}
              </Form.Label>
            </Col>
            <Col sm={7}>
              <Form.Control
                type={showOldPassword ? 'text' : 'password'}
                name="oldpassword"
                value={userData.oldpassword}
                placeholder={t("usersetting:caption.oldpassword")}
                onChange={handleChange}
                isInvalid={!!errors.oldpassword}
              />
              <Form.Control.Feedback type="invalid">
                {errors.oldpassword}
              </Form.Control.Feedback>
            </Col>
            <Col sm={1}>
              <Button
                className="show-password-button"
                onClick={() => togglePasswordVisibility('oldpassword')}
                tabIndex={-1}
                type="button"
              >
                <i className={`bi bi-eye${showOldPassword ? '-slash' : ''}`} />
              </Button>
            </Col>
          </Form.Group>

          <Form.Group
            as={Row}
            className="mb-3"
            onFocus={() => setFocusPassword(true)}
            onBlur={() => setFocusPassword(false)}
          >
            <Col sm={3}>
              <Form.Label column sm={2} style={{ textAlign: 'center', color: 'white' }}>
                {t("usersetting:caption.newpassword")}
              </Form.Label>
            </Col>
            <Col sm={7}>
              <Form.Control
                type={showNewPassword ? 'text' : 'password'}
                name="newpassword"
                value={userData.newpassword}
                onChange={handleChange}
                placeholder={t("usersetting:caption.newpassword")}
                isInvalid={!!errors.newpassword}
              />
              <Form.Control.Feedback type="invalid">
                {errors.newpassword}
              </Form.Control.Feedback>
            </Col>
            <Col sm={1}>
              <Button
                className="show-password-button"
                onClick={() => togglePasswordVisibility('newpassword')}
                tabIndex={-1}
                type="button"
              >
                <i className={`bi bi-eye${showNewPassword ? '-slash' : ''}`} />
              </Button>
            </Col>
          </Form.Group>

          <Form.Group
            as={Row}
            className="mb-3"
            onFocus={() => setFocusPassword(true)}
            onBlur={() => setFocusPassword(false)}
          >
            <Col sm={3}>
              <Form.Label column sm={2} style={{ textAlign: 'center', color: 'white' }}>
                {t("usersetting:caption.confirmpassword")}
              </Form.Label>
            </Col>
            <Col sm={7}>
              <Form.Control
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmpassword"
                value={userData.confirmpassword}
                placeholder={t("usersetting:caption.confirmpassword")}
                onChange={handleChange}
                isInvalid={!!errors.confirmpassword}
              />
              <Form.Control.Feedback type="invalid">
                {errors.confirmpassword}
              </Form.Control.Feedback>
            </Col>
            <Col sm={1}>
              <Button
                className="show-password-button"
                onClick={() => togglePasswordVisibility('confirmpassword')}
                tabIndex={-1}
                type="button"
              >
                <i className={`bi bi-eye${showConfirmPassword ? '-slash' : ''}`} />
              </Button>
            </Col>
          </Form.Group>
        </Col>

        <Col sm={3}>
          {focusPassword && (
            <>
              <div className="password-strength-bar-div">
                <div>{t("usersetting:messages.passwordstrength")}</div>
                <ProgressBar
                  variant={passwordStrength.score === 0 ? 'danger' :
                    passwordStrength.score === 1 ? 'danger' :
                      passwordStrength.score === 2 ? 'danger' :
                        passwordStrength.score === 3 ? 'warning' :
                          passwordStrength.score === 4 ? 'warning' :
                            passwordStrength.score === 5 ? 'success' : 'null'
                  }
                  now={passwordStrength.score === 0 ? '0' :
                    passwordStrength.score === 1 ? '20' :
                      passwordStrength.score === 2 ? '40' :
                        passwordStrength.score === 3 ? '60' :
                          passwordStrength.score === 4 ? '80' :
                            passwordStrength.score === 5 ? '100' : 'null'
                  }
                />
              </div><br />
              <ul className="password-requirements">
                <li className={passwordStrength.checks.length ? 'valid' : ''}>
                  {passwordStrength.checks.length ? '✅' : '❌'} {t("usersetting:messages.password8characters")}
                </li>
                <li className={passwordStrength.checks.uppercase ? 'valid' : ''}>
                  {passwordStrength.checks.uppercase ? '✅' : '❌'} {t("usersetting:messages.passwordoneup")}
                </li>
                <li className={passwordStrength.checks.lowercase ? 'valid' : ''}>
                  {passwordStrength.checks.lowercase ? '✅' : '❌'} {t("usersetting:messages.passwordonelow")}
                </li>
                <li className={passwordStrength.checks.number ? 'valid' : ''}>
                  {passwordStrength.checks.number ? '✅' : '❌'} {t("usersetting:messages.passwordonenum")}
                </li>
                <li className={passwordStrength.checks.special ? 'valid' : ''}>
                  {passwordStrength.checks.special ? '✅' : '❌'} {t("usersetting:messages.passwordonespecial")}
                </li>
              </ul>
            </>
          )}
        </Col>
      </Row>

      <Row className="row-submit-change-password">
        <Col className="d-flex justify-content-end">
          <Button variant="primary" type="submit">
            {t("usersetting:button.caption.submit")}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default ChangePassword;